/** Secondary locale. Keys mirror `ro.ts` exactly. */
export default {
  common: {
    unexpected: 'Something went wrong. Try again.',
    offline: 'We could not reach the server. Try again.',
    appName: 'SHIFT',
    retry: 'Retry',
    signOut: 'Sign out',
    loading: 'Loading…',
  },
  nav: {
    account: 'My account',
    jobs: 'My jobs',
    book: 'Book',
    addresses: 'My addresses',
    referrals: 'Invite a friend',
    cards: 'Cards',
    company: 'My company',
    help: 'Help',
  },
  auth: {
    title: 'Sign in',
    subtitle: 'Book home and facility services, paid securely.',
    email: 'Email or phone',
    password: 'Password',
    submit: 'Continue',
    emailRequired: 'Enter your email or phone number.',
    passwordRequired: 'Enter your password.',
    wrongApp: 'This is a worker or firm account. Sign in to the firm portal instead of here.',
  },
  register: {
    title: 'Create your account',
    subtitle: 'You need an account to book a service and follow the job.',
    name: 'Full name',
    nameRequired: 'Enter your name.',
    email: 'Email',
    emailRequired: 'Enter a valid email address.',
    phone: 'Phone (optional)',
    phoneHint: 'Used so the worker can reach you on site.',
    password: 'Password',
    passwordHint: 'At least {min} characters.',
    passwordTooShort: 'The password must be at least {min} characters.',
    submit: 'Create account',
    haveAccount: 'Already have an account?',
    signIn: 'Sign in',
    noAccount: 'No account yet?',
    createAccount: 'Create one',
    problem: {
      EMAIL_ALREADY_REGISTERED: 'An account with this email already exists. Try signing in.',
      VALIDATION_FAILED: 'Check the details you entered and try again.',
      UNEXPECTED: 'We could not create the account. Try again.',
    },
  },
  addresses: {
    shareWithCompany: 'A company address',
    shareWithCompanyHint:
      'Colleagues will see it and can book to it. Personal addresses stay yours alone.',
    search: 'Search for the address',
    searchExplainer:
      'Type the street and city, then pick from the list. The coordinates are filled in for you.',
    searchEmpty: 'No address found. Try wording it differently, or use your device location.',
    positionReady: 'Address located.',
    title: 'Your addresses',
    subtitle: 'The address tells the firm where to come, and decides which firms can take the job.',
    addTitle: 'Add an address',
    default: 'Default',
    locationExplainer:
      'We save the address using your device’s position, so add it from that place. ' +
      'If you are somewhere else, add it when you get there.',
    locate: 'Use my location',
    located: 'Location captured',
    accuracy: 'Accurate to about {metres} m.',
    needsLocation: 'Capture the location first, so the worker knows where to come.',
    addressLine: 'Street and number',
    addressRequired: 'Enter the street and number.',
    city: 'City',
    cityRequired: 'Enter the city.',
    label: 'Name (optional) — e.g. Home, Office',
    floor: 'Floor',
    apartment: 'Flat',
    accessNotes: 'How to get in (optional)',
    accessNotesHint: 'Entry code, which door, where to park.',
    save: 'Save address',
    problem: {
      DENIED:
        'We were not given access to your location. Allow it in your browser settings and try again.',
      UNAVAILABLE: 'We could not get your location. Check your signal and try again.',
      UNSUPPORTED: 'This browser cannot report a location. Try from a phone.',
      INACCURATE:
        'The location is too imprecise ({accuracy} m) to send a worker to. ' +
        'Step outside or try again in a few seconds.',
      REFUSED: 'We could not save the address. Check the details and try again.',
    },
  },
  home: {
    title: 'Your jobs',
    subtitle: 'What is happening now, and what is done.',
    newBooking: 'Book a service',
    noJobs: 'You have no jobs yet.',
    emergency: 'Emergency',
  },
  booking: {
    noServices: 'No services are available right now.',
    otherServices: 'Other services',
    changeService: 'Change service',
    estimatedMinutes: 'about {minutes} min',
    title: 'Book a service',
    subtitle: 'Pick the service and address, send a few photos. You pay at the end.',
    service: 'Service',
    address: 'Address',
    photos: 'Photos ({min}–{max})',
    photosHint: 'Helps the firm arrive prepared and shortens the visit.',
    description: 'Details (optional)',
    emergency: 'Maximum urgency',
    emergencyHint: 'Reaches firms sooner, at a higher rate.',
    holdExplainer: 'We hold the amount on your card now and take it only once the job is done.',
    submit: 'Send the booking',
    uploading: 'Uploading photos…',
    booking: 'Creating the booking…',
    paying: 'Holding the amount on your card…',
    noAddress: 'Add an address before booking.',
    noCard: 'Add a card before you can book. The amount is only held when you send the order.',
    addCard: 'Add a card',
    addAddress: 'Add an address',
    retryPaysExistingJob: 'The booking already exists — retrying only resumes the payment.',
    couponCode: 'Promotion code',
    couponApply: 'Check',
    couponHint: 'Checked before you book, so the price you see is the price you pay.',
    couponApplied: '{discount} off. You pay {total}.',
  },
  cards: {
    title: 'My cards',
    subtitle: 'You pay with a saved card. The card details never reach us.',
    card: 'Card',
    isDefault: 'Default card',
    makeDefault: 'Use by default',
    remove: 'Remove',
    add: 'Add a card',
    newCard: 'New card',
    save: 'Save the card',
    cancel: 'Cancel',
    empty: 'You have no saved card. You need one before you can book.',
    securityNote:
      'The card is typed directly into the payment provider’s secure field. We keep only the last four digits.',
    holdNote: 'At booking we hold the amount on the card and take it only once the work is done.',
    problem: {
      notAvailable: 'Card payment is not available yet. We will be back shortly.',
      providerUnreachable:
        'The card form could not be loaded. Check your connection and try again.',
      unexpected: 'Something went wrong. Try again in a moment.',
    },
  },
  company: {
    title: 'Company account',
    subtitle: 'Everything your colleagues book lands on one monthly invoice.',
    cui: 'CUI',
    members: 'Who books on the company account',
    inviteEmail: 'Colleague’s email',
    invite: 'Send the invitation',
    inviteHint: 'Any member may invite. The link is single-use and expires in 14 days.',
    invited: 'Invitation sent to {email}.',
    register: 'Register your company',
    registerHint:
      'The CUI is checked against the company registry. A struck-off or insolvent company cannot be billed.',
    cuiField: 'CUI',
    cuiHint: 'With or without RO.',
    billingEmail: 'Billing email',
    billingEmailHint: 'Where the monthly invoice is sent.',
    tradeName: 'Trading name (optional)',
    tradeNameHint: 'By default the registry’s name is used — that is what appears on the invoice.',
    registerAction: 'Register',
  },
  join: {
    title: 'Join the company account',
    subtitle: 'You have been invited to book on a company’s account.',
    password: 'Password',
    passwordHint:
      'If you already have a SHIFT account on this address, use the password you have. Otherwise choose one (at least 10 characters).',
    name: 'Your name',
    nameHint: 'Only needed if your account is being created now.',
    submit: 'Accept the invitation',
  },
  assistant: {
    title: 'Ask about your jobs',
    hint: 'Answers only from your own jobs and the published FAQ. It cannot book, change or cancel anything.',
    question: 'Your question',
    ask: 'Ask',
    grounded: 'Answered from {count} of your jobs.',
    fromFaq: 'Answered from the FAQ — you have no jobs yet.',
  },
  firmSignup: {
    title: 'Register your firm',
    subtitle: 'Take jobs from clients near you, with payment guaranteed by the platform.',
    cui: 'CUI',
    cuiHint: 'With or without RO. We check it against the company registry.',
    email: 'Contact email',
    emailHint: 'Where we send the activation link once you are approved.',
    phone: 'Phone (optional)',
    submit: 'Send the application',
    whatHappens:
      'We check the company against the registry, then the application goes for approval. We email you either way.',
    queued: '{name} is registered and waiting for approval.',
    categories: 'Services you qualify for, from your CAEN codes:',
    noCategories:
      'Your registered CAEN codes do not cover any service on the platform. The application stands — write to us if the firm holds further codes.',
    nextStep: 'Once approved, we send the activation link to {email}.',
    backToLogin: 'Back to sign in',
    problem: {
      INVALID_CUI: 'That CUI is not valid. Check the digits.',
      COMPANY_NOT_FOUND: 'No company with that CUI was found in the registry.',
      COMPANY_NOT_TRADING:
        'The registry reports this company as struck off, dissolved or insolvent. We cannot register a company that cannot legally trade.',
      TENANT_ALREADY_REGISTERED:
        'This firm is already registered. Check your email, or write to us.',
      VALIDATION_FAILED: 'Check the details you entered.',
      UNEXPECTED: 'Something went wrong. Try again in a moment.',
    },
  },
  help: {
    title: 'Frequently asked questions',
    subtitle: 'How booking, payment and the guarantee work.',
    empty: 'Nothing has been published here yet.',
  },
  referrals: {
    title: 'Invite a friend',
    subtitle: 'Your friend gets money off their first booking; you get yours once they pay for it.',
    yourCode: 'Your code',
    copy: 'Copy',
    copied: 'Copied',
    invited: 'used the code',
    rewarded: 'booked and paid',
    earned: 'you have earned',
    paidOnFirstJob:
      'Your reward arrives once your friend pays for their first job — not when they sign up.',
    haveACode: 'Got a code?',
    enterCode: 'Your friend’s code',
    enterCodeHint: 'Spaces and lower case are fine.',
    use: 'Use the code',
    redeemed: 'Done. Your discount code is {code} — use it on your first booking.',
  },
  job: {
    // Shown while the job is still loading and its service name is unknown.
    title: 'Job',
    call: 'Call the worker',
    callHint: 'A temporary number — neither of you sees the other’s.',
    address: 'Address',
    firm: 'Firm',
    worker: 'Worker',
    eta: 'Estimated arrival',
    total: 'Total',
    includesExtras: 'includes {extras} in extras',
    timeline: 'History',
    invoice: 'Invoice',
    invoiceReady: 'The firm has issued the invoice.',
    finished: 'This job is closed.',
  },
  noShow: {
    prompt: 'The worker has not arrived by the estimated time. What would you like to do?',
    keepWaiting: 'Keep waiting',
    cancelFree: 'Cancel, free',
  },
  rating: {
    title: 'How did it go?',
    comment: 'Comment (optional)',
    submit: 'Submit rating',
    tipHint: 'A tip reaches the firm whole — the platform takes no commission on it.',
    tipAmount: 'Tip (bani)',
    tipSubmit: 'Send tip',
  },
  jobStatus: {
    DRAFT: 'Draft',
    PAYMENT_PENDING: 'Processing payment',
    PAYMENT_FAILED: 'Payment failed',
    SEARCHING_FOR_FIRM: 'Finding a firm',
    NO_COVERAGE: 'No firm available',
    ACCEPTED_BY_FIRM: 'Taken by a firm',
    ASSIGNED_TO_WORKER: 'Worker assigned',
    WORKER_EN_ROUTE: 'Worker on the way',
    IN_PROGRESS: 'In progress',
    CHANGE_ORDER_PENDING: 'Waiting on your approval for extras',
    CANCELLATION_REQUESTED: 'The firm asked to cancel',
    COMPLETED: 'Completed',
    DISPUTED: 'Disputed',
    WARRANTY_CLAIMED: 'Warranty claimed',
    CANCELLED_BY_CLIENT: 'Cancelled by you',
    CANCELLED_BY_FIRM: 'Cancelled by the firm',
    CANCELLED_NO_SHOW: 'Cancelled — the worker never arrived',
    CANCELLED_ADMIN: 'Cancelled by the platform',
  },
  notFound: {
    title: 'Page not found',
    subtitle: 'Check the address or head back home.',
    back: 'Back home',
  },
} as const;
